import React, { useEffect } from "react"
import { i18n } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { useDispatch } from "react-redux"

import MyFavorites from "@/components/MyFavorites/v1/MyFavorites"
import usePageAuth from "@/hooks/usePageAuth"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import {
  footerUrl,
  headerUrl,
  MyFavoritesPageDataUrl,
  MyListsPageDataUrl,
} from "@/constants"
import AemGrid from "@/components/AemGrid"
import MyGenericFiller from "@/components/MyGenericFiller"
import { aemAxiosInstance } from "@/constants/api"
import { getClienIP, getDataLayerObj, filterBackEndJson } from "@/utils/helper"

import {
  setDatalayerPageName,
  setGbhDataLayerCustom,
} from "@/store/features/genericSlice"
import UpSellInstallServices from "@/components/Shared/UpsellInstallServices"

const MyFavoritesPage = ({
  tmpData: data = {},
  headertmp: header = {},
  footertmp: footer = {},
  globalScript,
  footerScript,
  analysticsScript,
  analyticsData = {},
  datalayerPageName = "",
  gbhDataLayerCustom = {},
  siteName = "",
  query = {},
}) => {
  const dispatch = useDispatch()
  usePageAuth()
  useEffect(() => {
    getDataLayerObj(analyticsData)
  }, [analyticsData])

  useEffect(() => {
    dispatch(setDatalayerPageName(datalayerPageName))
    dispatch(setGbhDataLayerCustom(gbhDataLayerCustom))

    return () => {
      dispatch(setDatalayerPageName(""))
      dispatch(setGbhDataLayerCustom(""))
    }
  }, [])

  return (
    <>
      <GlobalPageHeader
        header={header}
        title={data.title}
        globalScripting={globalScript}
        analysticsScript={analysticsScript}
      />
      <AemGrid
        data={data}
        count={0}
        myFavorites={data => (
          <MyFavorites
            data={data}
            header={header}
            siteName={siteName}
            query={query}
          />
        )}
        myGenericFiller={data => (
          <MyGenericFiller data={data} title="listTitle" />
        )}
        upSellContainer={data => <UpSellInstallServices authData={data} />}
      />
      <GlobalPageFooter footer={footer} footerScripting={footerScript} />
    </>
  )
}

/**
 * Get ServerSideProps together.
 * @param {Object} request The first number.
 * @return {object} The props.
 */
export async function getServerSideProps({ req, locale, query }) {
  if (query?.slug[0] !== "my-favorites" && query?.slug[0] !== "my-lists") {
    return {
      notFound: true,
    }
  }

  await i18n?.reloadResources()
  const urlPath = req.url.split("?")[0] ?? ""
  const ip = getClienIP(req)
  const { data } = await aemAxiosInstance({
    url: `/${locale}${
      query?.slug[0] === "my-favorites"
        ? MyFavoritesPageDataUrl
        : MyListsPageDataUrl
    }`,
    method: "GET",
  })
  const { data: header } = await aemAxiosInstance({
    url: `${data.headerExpFrg ? data.headerExpFrg : headerUrl}.model.json`,
    method: "GET",
  })

  const { data: footer } = await aemAxiosInstance({
    url: `${data.footerExpFrg ? data.footerExpFrg : footerUrl}.model.json`,
    method: "GET",
  })
  const globalScript = data?.globalScript || null
  const footerScript = data?.footerScript || null
  const topHeadScript = data?.topHeadScript ? data?.topHeadScript : null
  const analyticsScriptClass = data?.analyticsScriptClass
    ? data?.analyticsScriptClass
    : null

  const gbhDataLayerCustom = {}
  if (data?.datalayerPageName) {
    gbhDataLayerCustom.datalayerPageName = data?.datalayerPageName
  }
  if (data?.eventPageType) {
    gbhDataLayerCustom.eventPageType = data?.eventPageType
  }
  if (data?.pageType) {
    gbhDataLayerCustom.pageType = data?.pageType
  }
  const analysticsScript = data?.adobeAnalytic || null
  let tmpData = {}
  if (data.pageComponentProperties) {
    tmpData = data.pageComponentProperties
  } else {
    tmpData = data
  }
  const analyticsData = data?.data
  const datalayerPageName = data?.datalayerPageName
  tmpData = filterBackEndJson(tmpData)
  const headertmp = filterBackEndJson(header)
  const footertmp = filterBackEndJson(footer)
  const metaContent = data?.metaContent ?? ""
  const canonicalUrl = data?.canonicalUrl ?? ""
  const siteName = data?.siteName
  return {
    props: {
      ip,
      tmpData,
      headertmp,
      footertmp,
      globalScript,
      footerScript,
      topHeadScript,
      analysticsScript,
      analyticsScriptClass,
      analyticsData,
      datalayerPageName,
      gbhDataLayerCustom,
      urlPath,
      metaContent,
      canonicalUrl,
      siteName,
      query,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  }
}

export default MyFavoritesPage
